import type { FC } from 'react';
import { useCallback } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Swatch } from '../Swatch';

export interface BaseSwatch {
    color?: string;
    colorName?: string;
    productId: string;
}

export interface BaseSwatchesListProps {
    id?: string;
    swatches: BaseSwatch[];
    activeId?: string;
    onChange: (activeProductId: string) => void;
    size?: 'small' | 'default';
    renderFirstX?: number;
    onMouseEnter?: (productId?: string) => void;
    onMouseLeave?: (productId?: string) => void;
}

export const BaseSwatchesList: FC<BaseSwatchesListProps> = ({
    id,
    swatches,
    activeId,
    onChange,
    size = 'default',
    renderFirstX = undefined,
    onMouseEnter,
    onMouseLeave,
}) => {
    const handleClick = useCallback(
        (productId?: string): void => {
            if (productId) {
                onChange(productId);
            }
        },
        [onChange],
    );

    return (
        <Box
            gap={size === 'default' ? '13px' : 1}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            data-testid={`swatches-list-container${id ? `-${id}` : ''}`}
            role="list"
        >
            {swatches
                .slice(0, renderFirstX ?? swatches.length)
                .map(({ productId, color, colorName }, index) => (
                    <Flex
                        key={productId}
                        padding={size === 'default' ? undefined : 1}
                        role="listitem"
                    >
                        <Swatch
                            id={`product-variant-${productId}-swatch-${index}-button${
                                productId === activeId ? '-active' : ''
                            }`}
                            isActive={productId === activeId}
                            color={color ?? 'transparent'}
                            productId={productId}
                            onClick={handleClick}
                            value={colorName}
                            size={size}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        />
                    </Flex>
                ))}
        </Box>
    );
};

BaseSwatchesList.defaultProps = {
    activeId: undefined,
    size: 'default',
    renderFirstX: undefined,
    onMouseEnter: undefined,
    onMouseLeave: undefined,
    id: undefined,
};
